import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import type { Column } from '@tanstack/react-table'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Separator } from '@/shared/components/ui/separator'
import { cn } from '@/shared/lib/utils'
import type { FilterValue } from '@/shared/lib/api/api-types'

interface SetFilterProps<TData, TValue> {
  column: Column<TData, TValue>
  onClose: () => void
}

export function SetFilter<TData, TValue>({ column, onClose }: SetFilterProps<TData, TValue>) {
  const { t } = useTranslation()
  const current = column.getFilterValue() as Extract<FilterValue, { filterType: 'set' }> | undefined
  const selected = new Set(current?.values ?? [])

  const staticOptions = (column.columnDef.meta?.filterStaticOptions ?? []).map(String).filter(Boolean)
  const fetchOptions = column.columnDef.meta?.filterFetchOptions
  const single = column.columnDef.meta?.filterSingle ?? false
  const labelOf = column.columnDef.meta?.filterOptionLabel ?? ((value: string) => value)
  const [options, setOptions] = useState<string[]>(staticOptions)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (fetchOptions && options.length === 0) {
      setLoading(true)
      fetchOptions()
        .then((vals) => setOptions(vals.map(String).filter(Boolean)))
        .catch(console.error)
        .finally(() => setLoading(false))
    }
  }, [fetchOptions, options.length])

  const filtered = options
    .map((option) => String(option))
    .filter((option) => labelOf(option).toLowerCase().includes(search.toLowerCase()))

  function toggle(value: string) {
    // Single-select: a click replaces the selection (or clears it when the
    // already-selected option is clicked again).
    if (single) {
      if (selected.has(value)) {
        column.setFilterValue(undefined)
      } else {
        column.setFilterValue({ filterType: 'set', values: [value] })
      }
      return
    }
    const next = new Set(selected)
    if (next.has(value)) {
      next.delete(value)
    } else {
      next.add(value)
    }
    if (next.size === 0) {
      column.setFilterValue(undefined)
    } else {
      column.setFilterValue({ filterType: 'set', values: Array.from(next) })
    }
  }

  function selectAll() {
    column.setFilterValue({ filterType: 'set', values: [...options] })
  }

  function clearAll() {
    column.setFilterValue(undefined)
  }

  return (
    <div className="flex flex-col w-52">
      <div className="p-2">
        <Input
          className="h-7 text-xs"
          placeholder={t('dataTable.search')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          autoFocus
        />
      </div>
      <Separator />
      <div className="flex gap-1 px-2 py-1">
        {!single && (
          <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={selectAll}>
            {t('dataTable.all')}
          </Button>
        )}
        <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={clearAll}>
          {t('dataTable.none')}
        </Button>
      </div>
      <Separator />
      <div className="max-h-48 overflow-y-auto py-1">
        {loading && (
          <p className="px-3 py-2 text-xs text-muted-foreground">{t('dataTable.loading')}</p>
        )}
        {!loading && filtered.length === 0 && (
          <p className="px-3 py-2 text-xs text-muted-foreground">{t('dataTable.noOptions')}</p>
        )}
        {!loading &&
          filtered.map((option) => (
            <div
              key={option}
              role="button"
              tabIndex={0}
              className={cn(
                'flex items-center gap-2 px-3 py-1 cursor-pointer hover:bg-muted/50 text-sm',
                selected.has(option) && 'bg-muted/30',
              )}
              onClick={() => toggle(option)}
              onKeyDown={(event) => event.key === 'Enter' && toggle(option)}
            >
              <Checkbox
                checked={selected.has(option)}
                className="h-3.5 w-3.5 pointer-events-none"
              />
              <span className="truncate">{labelOf(option)}</span>
            </div>
          ))}
      </div>
      <Separator />
      <div className="p-2">
        <Button size="sm" className="h-6 w-full text-xs" onClick={onClose}>
          {t('dataTable.close')}
        </Button>
      </div>
    </div>
  )
}
